import { storeRedirectData } from "../../../utils/command/commandStateUtils";
import {
  logRedirectStart,
  logRedirectResult,
  logRedirectError,
} from "../../../utils/core/debugUtils";
import { displayRedirectStepOutro } from "./redirectOutputHandlers";
import useCommandFlowStore from "../../../store/commandFlowStore";

/**
 * Handles redirect command execution with stepOutro display
 * @param {Object} result - The command result containing nextCommand and optional output
 * @param {Object} engine - The command flow engine
 * @param {string} activeCommandName - The name of the currently active command
 * @returns {Promise<Object>} The redirect command result
 */
export async function handleRedirectCommand(result, engine, activeCommandName) {
  // Track the parent command for "back" navigation across redirects. Each
  // redirect pushes the caller so the user can walk back the chain one hop
  // per "back" input (see handleBackCommandWithSetters).
  if (activeCommandName) {
    if (!Array.isArray(engine.redirectParentChain)) {
      engine.redirectParentChain = [];
    }
    engine.redirectParentChain.push(activeCommandName);
  }

  // Store redirect data using utility function
  storeRedirectData(
    result.nextCommand,
    result.nextCommandData || {},
    activeCommandName
  );

  // First, display the stepOutro if there is one
  if (result.output) {
    await displayRedirectStepOutro(result.output);
    // displayRedirectStepOutro's typing effect clears isLoading when done.
    // Re-set isLoading so the loading indicator stays visible while the
    // next command starts (prevents the ">" prompt from flashing).
    useCommandFlowStore.setState({ isLoading: true });
  }

  // Then start the new command
  return await executeRedirectCommand(result, engine);
}

/**
 * Executes the redirect command with timeout and error handling
 * @param {Object} result - The command result containing nextCommand data
 * @param {Object} engine - The command flow engine
 * @returns {Promise<Object>} The redirect command result
 */
export async function executeRedirectCommand(result, engine) {
  // Do NOT toggle isLoading here — the calling context already manages it.
  // Toggling it between typing effects causes a loading indicator flash.
  logRedirectStart(result.nextCommand, result.nextCommandData, engine);

  let redirectResult;
  try {
    console.log("🔄 About to call engine.startCommand with:", {
      command: result.nextCommand,
      data: result.nextCommandData,
      engineInSession: engine.isInSession(),
    });

    // PRESERVE the current session data before ending the session
    let dataToPreserve = {};
    if (engine.isInSession()) {
      const sessionInfo = engine.sessionCoordinator.getSessionInfo();
      dataToPreserve = sessionInfo.sessionData || {};
      console.log("📦 Preserving session data:", dataToPreserve);

      // END the current session BEFORE starting the redirect command
      // This ensures the engine doesn't have conflicting session states
      console.log("⚠️ Ending previous session before redirect");
      // Keep isLoading true so the loading indicator stays visible while
      // clearCommandState nulls currentPrompt — prevents the ">" prompt
      // from flashing between chained frames.
      useCommandFlowStore.setState({ isLoading: true });
      engine.endSession();
    }

    // MERGE the preserved data with the redirect data
    // Redirect data takes precedence, but preserves previous command's data
    const mergedData = {
      ...dataToPreserve,
      ...result.nextCommandData,
    };

    // Add a timeout to detect if the call is hanging
    // (generous limit to accommodate typing effects during intro)
    const timeoutPromise = new Promise((_, reject) => {
      setTimeout(
        () => reject(new Error("startCommand timeout after 60 seconds")),
        60000
      );
    });

    const startCommandPromise = engine.startCommand(
      result.nextCommand,
      mergedData,
      { isRedirect: true }
    );

    redirectResult = await Promise.race([startCommandPromise, timeoutPromise]);

    console.log("🔄 engine.startCommand returned:", {
      hasPrompt: !!redirectResult.prompt,
      hasOutput: !!redirectResult.output,
      hasError: !!redirectResult.error,
      fullResult: redirectResult,
    });

    logRedirectResult(redirectResult);
  } catch (error) {
    logRedirectError(error);
    throw error;
  }

  return redirectResult;
}
